import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopPromoBar from '../components/TopPromoBar';
import Header from '../components/Header';
import Footer from '../components/Footer';
import MobileSignInBanner from '../components/MobileSignInBanner';
import HomePage from './HomePage';
import Toast from '../components/Toast';
import CartDrawer from '../components/CartDrawer';
import FlyingItem from '../components/FlyingItem';
import QuickViewModal from '../components/QuickViewModal';
import { isProfileComplete } from '../utils/profileCompletion';
import config from '../config';

function ShopApp({
  logo,
  products,
  loading,
  error,
  cart,
  wishlist,
  toast,
  isCartOpen,
  setIsCartOpen,
  flyingItem,
  searchQuery,
  setSearchQuery,
  activeFilter,
  setActiveFilter,
  handleAddToCart,
  handleToggleWishlist,
  handleQuickView,
  handleUpdateQuantity,
  handleRemoveItem,
  handleCloseToast,
  handleFlyingComplete,
  quickViewProduct,
  isQuickViewOpen,
  setIsQuickViewOpen,
  setQuickViewProduct,
  cartButtonRef,
  user,
  onLoginSuccess,
  onLogout,
  pagination,
  loadMoreProducts,
}) {
  const { app } = config;
  const APP_NAME = app.name;
  const navigate = useNavigate();
  
  // Manage auth modal state at ShopApp level so both Header and MobileSignInBanner can use it
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [shouldRedirectToCheckout, setShouldRedirectToCheckout] = useState(false);

  // Handle cart click - check profile completion first
  const handleCartClick = () => {
    // If user is logged in, check profile completion
    if (user) {
      const userRole = user?.role?.name;
      const isAdmin = userRole === 'super_admin' || userRole === 'administrator' || userRole === 'manager';
      const isCourier = userRole === 'courier';
      
      // Skip check for admins/couriers
      if (!isAdmin && !isCourier && !isProfileComplete(user)) {
        // Redirect to profile page instead of opening cart
        navigate('/profile');
        return;
      }
    }
    
    // Open cart if profile is complete or user is not logged in
    setIsCartOpen(true);
  };

  return (
    <div className="App min-h-screen flex flex-col">
      <TopPromoBar appName={APP_NAME} />
      <Header 
        logo={logo} 
        appName={APP_NAME}
        cartCount={cart.reduce((sum, item) => sum + (item.quantity || 1), 0)}
        onCartClick={handleCartClick}
        cartButtonRef={cartButtonRef}
        searchQuery={searchQuery}
        onSearchChange={(query) => {
          setSearchQuery(query);
          if (query.trim()) {
            setActiveFilter(null);
          }
        }}
        activeFilter={activeFilter}
        onFilterChange={setActiveFilter}
        onCategoryFilterChange={(categoryId) => {
          if (categoryId) {
            setActiveFilter(`category-${categoryId}`);
            setSearchQuery('');
          } else {
            setActiveFilter(null);
          }
        }}
        user={user}
        onLoginSuccess={(user, token, options) => {
          // Pass through options from AuthModal directly
          // AuthModal will pass redirectAfterLogin (true for checkout, false for user menu)
          // and redirectAfterRegister (true for registration, undefined/false for login)
          // Only override redirectAfterLogin if we're in checkout flow
          const finalOptions = shouldRedirectToCheckout 
            ? { ...options, redirectAfterLogin: true }
            : options;
          setShouldRedirectToCheckout(false);
          // Pass through user, token, and options (for redirect flags)
          onLoginSuccess(user, token, finalOptions);
        }}
        onLogout={onLogout}
        authModalOpen={authModalOpen}
        setAuthModalOpen={(open) => {
          setAuthModalOpen(open);
          if (!open) {
            setShouldRedirectToCheckout(false); // Reset flag when modal closes
          }
        }}
        redirectAfterLogin={shouldRedirectToCheckout}
        redirectAfterRegister={true}
      />
      {/* Mobile Sign In Banner - Only show on mobile when user is not logged in */}
      {!user && (
        <MobileSignInBanner onSignInClick={() => setAuthModalOpen(true)} />
      )}
      <div className={`flex-grow pt-[100px] sm:pt-[120px] ${!user ? 'pt-[150px] sm:pt-[120px]' : ''}`}>
        {loading && (
          <div className="max-w-screen-2xl mx-auto my-4 sm:my-6 md:my-8 px-3 sm:px-4 md:px-8 text-center">
            <p className="text-secondary text-sm sm:text-base md:text-lg">Loading products...</p>
          </div>
        )}
        {error && (
          <div className="max-w-screen-2xl mx-auto my-4 sm:my-6 md:my-8 px-3 sm:px-4 md:px-8 text-center">
            <p className="text-primary text-sm sm:text-base md:text-lg">{error}</p>
          </div>
        )}
        {!loading && !error && (
          <HomePage 
            products={products} 
            onAddToCart={handleAddToCart}
            onToggleWishlist={handleToggleWishlist}
            onQuickView={handleQuickView}
            wishlist={wishlist}
            searchQuery={searchQuery}
            activeFilter={activeFilter}
            pagination={pagination}
            loadMoreProducts={loadMoreProducts}
          />
        )}
      </div>
      <Toast 
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={handleCloseToast}
      />
      <CartDrawer
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cart={cart}
        onUpdateQuantity={handleUpdateQuantity}
        onRemoveItem={handleRemoveItem}
        user={user}
        onOpenAuthModal={() => {
          setIsCartOpen(false); // Close cart drawer when opening auth modal
          setShouldRedirectToCheckout(true); // Set flag to redirect to checkout after login
          setAuthModalOpen(true);
        }}
      />
      <QuickViewModal
        isOpen={isQuickViewOpen}
        onClose={() => {
          setIsQuickViewOpen(false);
          setQuickViewProduct(null);
        }}
        product={quickViewProduct}
        onAddToCart={handleAddToCart}
        onToggleWishlist={handleToggleWishlist}
        isWishlisted={quickViewProduct ? wishlist.includes(quickViewProduct.id) : false}
        user={user}
        cart={cart}
        onOpenAuthModal={() => {
          setIsQuickViewOpen(false); // Close quick view modal when opening auth modal
          setQuickViewProduct(null);
          setShouldRedirectToCheckout(true); // Set flag to redirect to checkout after login
          setAuthModalOpen(true);
        }}
      />
      {flyingItem && (
        <FlyingItem
          startPos={flyingItem.start}
          endPos={flyingItem.end}
          onComplete={handleFlyingComplete}
        />
      )}
      <Footer />
    </div>
  );
}

export default ShopApp;

